import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { initFlags, loadRuntimeConfig } from '@eis/core';
import { App } from '../app/App';
import { loadAuthAdapter } from '../providers/auth-adapter';
import { createI18n } from '../i18n';
import { moduleRegistry } from '../app/modules';

/**
 * 12-factor SPA bootstrap (backbone §7): the compiled bundle is environment-agnostic and reads
 * `/config.json` at startup, so the same artifact promotes from local to prod unchanged.
 *
 * Order is deliberate: config → flags → i18n (needs the supported-language list from config) →
 * render. Nothing renders against a half-initialised environment.
 */
async function bootstrap(): Promise<void> {
  const container = document.getElementById('root');
  if (container === null) throw new Error('#root is missing from index.html');

  try {
    const config = await loadRuntimeConfig();
    await initFlags(config.flags);

    // Driven by RUNTIME config, not by build mode: that is the whole point of the 12-factor SPA.
    // The same artifact runs against mocks locally and against the real API in prod, and the E2E
    // suite therefore exercises the production bundle rather than a dev-only code path.
    // The import is dynamic, so MSW is a separate chunk that prod deployments never fetch.
    // `mockApi` keeps the mock under a real auth adapter (SDD-019 §12: the OIDC adapter against the
    // mocked session endpoints); no deployed config sets it.
    if (config.auth.provider === 'mock' || config.mockApi === true) {
      const { startMockServiceWorker } = await import('./msw');
      await startMockServiceWorker();
    }

    const i18nInstance = await createI18n({
      supportedLanguages: config.i18n.supportedLanguages,
      defaultLanguage: config.i18n.defaultLanguage,
      moduleResources: moduleRegistry.modules
        .filter((module) => module.i18nResources !== undefined)
        .map((module) => ({
          namespace: module.i18nNamespaces[0] ?? module.id,
          resources: module.i18nResources ?? {},
        })),
    });

    // Built once, before the first render: the OIDC adapter is a lazy chunk (SDD-019).
    const adapter = await loadAuthAdapter(config);

    createRoot(container).render(
      <StrictMode>
        <App config={config} adapter={adapter} i18nInstance={i18nInstance} />
      </StrictMode>,
    );
  } catch (error) {
    // A blank page looks like a network stall, so name the actual failure. i18n may not have
    // loaded at this point, which is why this one string is not translated.
    container.innerHTML = `<div role="alert" style="font-family:system-ui;padding:2rem;max-width:40rem;margin:0 auto">
      <h1 style="font-size:1.25rem">Die Konsole konnte nicht starten</h1>
      <p style="color:#4E5A66">Der Startvorgang wurde mit folgendem Fehler abgebrochen.</p>
      <pre style="white-space:pre-wrap;font-size:.75rem;color:#8E2C2C">${escapeHtml(String(error))}</pre>
    </div>`;
    throw error;
  }
}

/** The message can contain a URL from a failed import; never inject it as markup. */
function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

void bootstrap();
